import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const num = (v) => { const n = parseFloat(String(v).replace(',', '.')); return isNaN(n) ? null : n; };

const CATEGORIA_ICONS = {
  'Contenedores y desechables': '📦',
  'Polvos': '🧪',
  'Frutos secos y otros': '🌰',
  'Lácteos y derivados': '🥛',
  'Panadería y repostería': '🍞',
  'Galletas y otros': '🍪',
  'Aseo y limpieza': '🧹',
  'Apliques y papelería': '📎',
};

export default function InventarioProduccion() {
  const { usuario, puedeVer } = useAuth();
  const esAdmin = puedeVer('gerencia', 'administracion');
  const esProduccion = puedeVer('produccion');

  const hoy = format(new Date(), 'yyyy-MM-dd');
  const [fecha, setFecha]           = useState(hoy);
  const [items, setItems]           = useState([]);
  const [valores, setValores]       = useState({});
  const [registro, setRegistro]     = useState(null);
  const [cargando, setCargando]     = useState(false);
  const [guardando, setGuardando]   = useState(false);
  const [historial, setHistorial]   = useState([]);
  const [vistaTab, setVistaTab]     = useState('formulario');
  const [categoriaAbierta, setCategoriaAbierta] = useState({});
  const [busqueda, setBusqueda]     = useState('');
  const [notas, setNotas]           = useState('');

  // Cargar items
  useEffect(() => {
    API.get('/produccion/items')
      .then(r => {
        setItems(r.data);
        // Abrir todas las categorías por defecto
        const cats = {};
        r.data.forEach(i => { cats[i.categoria] = true; });
        setCategoriaAbierta(cats);
      }).catch(() => {});
  }, []);

  // Cargar registro existente
  const cargarRegistro = useCallback(() => {
    if (!fecha) return;
    setCargando(true);
    API.get('/produccion/registro', { params: { fecha } })
      .then(r => {
        setRegistro(r.data);
        setNotas(r.data?.notas || '');
        if (r.data?.detalle) {
          const v = {};
          r.data.detalle.forEach(d => {
            v[d.item_id] = {
              cantidad_actual: d.cantidad_actual,
              ingreso_fecha:   d.ingreso_fecha ? d.ingreso_fecha.slice(0,10) : '',
              ingreso_cantidad: d.ingreso_cantidad,
              salida_fecha:    d.salida_fecha ? d.salida_fecha.slice(0,10) : '',
              salida_cantidad:  d.salida_cantidad,
              observacion:     d.observacion || '',
            };
          });
          setValores(v);
        } else {
          setValores({});
        }
      }).catch(() => { setRegistro(null); setValores({}); })
      .finally(() => setCargando(false));
  }, [fecha]);

  useEffect(() => { cargarRegistro(); }, [cargarRegistro]);

  // Historial
  useEffect(() => {
    API.get('/produccion/historial', { params: { limit: 10 } })
      .then(r => setHistorial(r.data)).catch(() => {});
  }, [registro]);

  const setValor = (itemId, campo, val) => {
    setValores(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [campo]: val } }));
  };

  const bloqueado = registro?.estado === 'enviado' && !esAdmin;

  const handleGuardar = async (enviar = false) => {
    setGuardando(true);
    try {
      const detalle = items.map(item => ({
        item_id:          item.id,
        cantidad_actual:  num(valores[item.id]?.cantidad_actual),
        ingreso_fecha:    valores[item.id]?.ingreso_fecha || null,
        ingreso_cantidad: num(valores[item.id]?.ingreso_cantidad),
        salida_fecha:     valores[item.id]?.salida_fecha || null,
        salida_cantidad:  num(valores[item.id]?.salida_cantidad),
        observacion:      valores[item.id]?.observacion || null,
      })).filter(d => d.cantidad_actual !== null || d.ingreso_cantidad !== null || d.salida_cantidad !== null || d.observacion);
      await API.post('/produccion/guardar', { fecha, notas, items: detalle, enviar });
      toast.success(enviar ? '✅ Inventario enviado' : '💾 Borrador guardado');
      cargarRegistro();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const handleCorregir = async () => {
    try {
      await API.post('/produccion/corregir', { registro_id: registro.id });
      toast.success('Desbloqueado para corrección');
      cargarRegistro();
    } catch { toast.error('Error'); }
  };

  // Agrupar y filtrar
  const itemsFiltrados = busqueda
    ? items.filter(i => i.nombre.toLowerCase().includes(busqueda.toLowerCase()) || i.codigo.toLowerCase().includes(busqueda.toLowerCase()))
    : items;

  const porCategoria = {};
  itemsFiltrados.forEach(item => {
    if (!porCategoria[item.categoria]) porCategoria[item.categoria] = [];
    porCategoria[item.categoria].push(item);
  });

  const estadoColor = {
    borrador: { bg: '#FEF3C7', color: '#92400E', label: 'Borrador' },
    enviado:  { bg: '#D1FAE5', color: '#065F46', label: 'Enviado ✓' },
  };

  const totalDiligenciados = items.filter(i => valores[i.id]?.cantidad_actual !== undefined && valores[i.id]?.cantidad_actual !== '' && valores[i.id]?.cantidad_actual !== null).length;

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>🏭 Inventario Producción</h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Control de materias primas e insumos</p>
        </div>
        {registro?.estado && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ padding: '6px 14px', borderRadius: 20, fontWeight: 700, fontSize: 13, background: estadoColor[registro.estado]?.bg, color: estadoColor[registro.estado]?.color }}>
              {estadoColor[registro.estado]?.label}
            </span>
            {registro.estado === 'enviado' && esAdmin && (
              <button onClick={handleCorregir} style={{ padding: '6px 14px', border: '2px solid #F59E0B', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#92400E' }}>
                ✏️ Permitir corrección
              </button>
            )}
          </div>
        )}
      </div>

      {/* Controles */}
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 18, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={st.label}>Fecha</div>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} max={hoy}
            style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }} />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={st.label}>Buscar ítem</div>
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Nombre o código..."
            style={{ width: '100%', padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 2 }}>
            {[{ id: 'formulario', label: '📝 Llenar' }, { id: 'historial', label: '📋 Historial' }].map(t => (
              <button key={t.id} onClick={() => setVistaTab(t.id)} style={{ padding: '6px 14px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13,
                fontWeight: vistaTab === t.id ? 700 : 500, background: vistaTab === t.id ? 'white' : 'transparent',
                color: vistaTab === t.id ? '#111827' : '#6B7280', boxShadow: vistaTab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {vistaTab === 'historial' ? (
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', fontWeight: 800, fontSize: 15 }}>📋 Últimos registros</div>
          {historial.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF' }}>Sin registros aún</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  <th style={th}>Fecha</th>
                  <th style={th}>Encargado</th>
                  <th style={th}>Ítems</th>
                  <th style={th}>Estado</th>
                  <th style={th}>Notas</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((h, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                    <td style={td}><strong>{format(new Date(h.fecha.slice(0,10)+'T12:00'), "d MMM yyyy", { locale: es })}</strong></td>
                    <td style={td}>{h.encargado_nombre || '—'}</td>
                    <td style={td}>{h.total_items || 0} ítems</td>
                    <td style={td}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: h.estado === 'enviado' ? '#D1FAE5' : '#FEF3C7', color: h.estado === 'enviado' ? '#065F46' : '#92400E' }}>
                        {h.estado === 'enviado' ? '✅ Enviado' : '📝 Borrador'}
                      </span>
                    </td>
                    <td style={{ ...td, fontSize: 12, color: '#6B7280' }}>{h.notas || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : cargando ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Cargando inventario...</div>
      ) : (
        <>
          {/* Progreso */}
          <div style={{ background: 'white', borderRadius: 12, padding: '12px 18px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                <span style={{ fontWeight: 600, color: '#374151' }}>Progreso</span>
                <span style={{ color: '#6B7280' }}>{totalDiligenciados} / {items.length} ítems</span>
              </div>
              <div style={{ background: '#F3F4F6', borderRadius: 8, height: 8, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 8, background: 'linear-gradient(90deg,#3B4E8C,#5B6FAC)', width: `${(totalDiligenciados / items.length) * 100}%`, transition: 'width 0.3s' }} />
              </div>
            </div>
            {bloqueado && (
              <span style={{ fontSize: 12, color: '#6B7280', fontStyle: 'italic' }}>🔒 Enviado — solo lectura</span>
            )}
          </div>

          {/* Notas del registro */}
          {!bloqueado && (
            <div style={{ background: 'white', borderRadius: 12, padding: '12px 18px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={st.label}>Observaciones generales</div>
              <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2} placeholder="Ej: Recibimos pedido de Levapan, faltó revisar sección polvos..."
                style={{ width: '100%', padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>
          )}

          {/* Categorías */}
          {Object.entries(porCategoria).map(([cat, catItems]) => {
            const abierta = categoriaAbierta[cat] !== false;
            const llenos = catItems.filter(i => valores[i.id]?.cantidad_actual !== undefined && valores[i.id]?.cantidad_actual !== '' && valores[i.id]?.cantidad_actual !== null).length;
            return (
              <div key={cat} style={{ background: 'white', borderRadius: 14, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                {/* Header categoría */}
                <div onClick={() => setCategoriaAbierta(p => ({ ...p, [cat]: !abierta }))}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', cursor: 'pointer', background: '#F9FAFB', borderBottom: abierta ? '1px solid #F3F4F6' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>{CATEGORIA_ICONS[cat] || '📋'}</span>
                    <span style={{ fontWeight: 800, fontSize: 15, color: '#111827' }}>{cat}</span>
                    <span style={{ fontSize: 12, color: '#6B7280', background: '#E5E7EB', padding: '2px 8px', borderRadius: 20 }}>
                      {llenos}/{catItems.length}
                    </span>
                  </div>
                  <span style={{ color: '#9CA3AF', fontSize: 18 }}>{abierta ? '▲' : '▼'}</span>
                </div>

                {abierta && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                          <th style={{ ...th, width: 80 }}>Código</th>
                          <th style={th}>Ítem</th>
                          <th style={{ ...th, width: 70 }}>Unidad</th>
                          <th style={{ ...th, width: 110, background: '#EFF6FF' }}>Cant. actual</th>
                          <th style={{ ...th, width: 100 }}>Fecha ingreso</th>
                          <th style={{ ...th, width: 100 }}>Cant. ingreso</th>
                          <th style={{ ...th, width: 100 }}>Fecha salida</th>
                          <th style={{ ...th, width: 100 }}>Cant. salida</th>
                          <th style={th}>Observación</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catItems.map((item, i) => {
                          const v = valores[item.id] || {};
                          const tieneCantidad = v.cantidad_actual !== undefined && v.cantidad_actual !== '' && v.cantidad_actual !== null;
                          return (
                            <tr key={item.id} style={{ borderTop: '1px solid #F3F4F6', background: tieneCantidad ? '#F0FDF4' : (i % 2 === 0 ? 'white' : '#FAFAFA') }}>
                              <td style={{ ...td, color: '#9CA3AF', fontSize: 11, fontFamily: 'monospace' }}>{item.codigo}</td>
                              <td style={{ ...td, fontWeight: 600 }}>{item.nombre}</td>
                              <td style={{ ...td, color: '#6B7280' }}>{item.unidad}</td>
                              <td style={{ ...td, background: '#EFF6FF' }}>
                                <input type="number" step="0.01" value={v.cantidad_actual ?? ''} disabled={bloqueado}
                                  onChange={e => setValor(item.id, 'cantidad_actual', e.target.value)}
                                  style={{ width: '100%', padding: '5px 8px', border: '2px solid #BFDBFE', borderRadius: 6, fontSize: 13, outline: 'none', textAlign: 'right', background: bloqueado ? '#F9FAFB' : 'white', fontWeight: 700 }} />
                              </td>
                              <td style={td}>
                                <input type="date" value={v.ingreso_fecha || ''} disabled={bloqueado}
                                  onChange={e => setValor(item.id, 'ingreso_fecha', e.target.value)}
                                  style={{ width: '100%', padding: '5px 6px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 11, outline: 'none', background: bloqueado ? '#F9FAFB' : 'white' }} />
                              </td>
                              <td style={td}>
                                <input type="number" step="0.01" value={v.ingreso_cantidad ?? ''} disabled={bloqueado}
                                  onChange={e => setValor(item.id, 'ingreso_cantidad', e.target.value)}
                                  style={{ width: '100%', padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13, outline: 'none', textAlign: 'right', background: bloqueado ? '#F9FAFB' : 'white' }} />
                              </td>
                              <td style={td}>
                                <input type="date" value={v.salida_fecha || ''} disabled={bloqueado}
                                  onChange={e => setValor(item.id, 'salida_fecha', e.target.value)}
                                  style={{ width: '100%', padding: '5px 6px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 11, outline: 'none', background: bloqueado ? '#F9FAFB' : 'white' }} />
                              </td>
                              <td style={td}>
                                <input type="number" step="0.01" value={v.salida_cantidad ?? ''} disabled={bloqueado}
                                  onChange={e => setValor(item.id, 'salida_cantidad', e.target.value)}
                                  style={{ width: '100%', padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13, outline: 'none', textAlign: 'right', background: bloqueado ? '#F9FAFB' : 'white' }} />
                              </td>
                              <td style={td}>
                                <input type="text" value={v.observacion || ''} disabled={bloqueado}
                                  onChange={e => setValor(item.id, 'observacion', e.target.value)}
                                  placeholder="Notas..."
                                  style={{ width: '100%', padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12, outline: 'none', background: bloqueado ? '#F9FAFB' : 'white' }} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* Botones guardar */}
          {!bloqueado && (
            <div style={{ display: 'flex', gap: 12, marginTop: 8, marginBottom: 32, flexWrap: 'wrap' }}>
              <button onClick={() => handleGuardar(false)} disabled={guardando}
                style={{ flex: 1, minWidth: 140, padding: '12px 20px', border: '2px solid #1a1a2e', borderRadius: 10, background: 'white', cursor: guardando ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>
                {guardando ? '⏳ Guardando...' : '💾 Guardar borrador'}
              </button>
              <button onClick={() => handleGuardar(true)} disabled={guardando}
                style={{ flex: 2, minWidth: 180, padding: '12px 20px', border: 'none', borderRadius: 10, background: guardando ? '#9CA3AF' : 'linear-gradient(135deg,#1a1a2e,#3B4E8C)', cursor: guardando ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, color: 'white' }}>
                {guardando ? '⏳ Enviando...' : '✅ Enviar inventario'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const th = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };
const td = { padding: '8px 12px', verticalAlign: 'middle' };
const st = { label: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 } };
